// Imports for external libraries go here.
import React, { FC, useMemo } from 'react';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Heading, Types, Points, RichText } from '@marriott/mi-ui-library';

import { PointsDetailsProps } from './PointsDetails.types';
import { StyledPointsDetails } from './PointsDetails.styles';

export const PointsDetailsConfig = {
  emptyLabel: 'Points Potential',
  isEmpty: () => true,
  resourceType: `/components/content/pointsdetails`,
};

/**
 * PointsDetailsComp - Organism component for displaying detailed points potential information
 *
 * Features:
 * - Displays main title, subtitle, points multiplier, and rich text description
 * - Accessible with screen reader support for card name
 * - Responsive layout with centered content
 * - Memoized for performance optimization
 *
 * @param props - PointsDetailsProps configuration
 * @returns React component or null if invalid
 */
export const PointsDetailsComp: FC<PointsDetailsProps> = React.memo((props: PointsDetailsProps) => {
  const { title, subTitle, noOfPoints, pointsDescription, componentID, cardName } = props;

  // Memoize unique component ID
  const uniqueId = useMemo(() => `pointsdetail${componentID}`, [componentID]);

  // Memoize aria-label for accessibility
  const ariaLabel = useMemo(() => {
    return cardName ? `Points details for ${cardName}` : 'Points details';
  }, [cardName]);

  // Memoize container classes
  const containerClasses = useMemo(
    () => clsx('pb-lg-5 pb-md-4 pb-2 mb-md-2 d-flex flex-column align-items-center t-background-color'),
    []
  );

  // Memoize heading classes
  const headingClasses = useMemo(() => clsx('text-center mt-5 pt-lg-5 pt-2 mb-md-5 mb-4 pb-2'), []);

  // Memoize subtitle classes
  const subtitleClasses = useMemo(() => clsx('text-center mt-md-5 mt-4 mb-3'), []);

  // Memoize content wrapper classes
  const contentWrapperClasses = useMemo(
    () => clsx('m-message-inline px-md-5 px-3 pb-lg-4 pb-3 pt-2 col-lg-10 col-12'),
    []
  );

  // Memoize description classes
  const descriptionClasses = useMemo(
    () => 'points-potentional-description text-center mx-lg-5 px-lg-4 pb-lg-3 pb-0',
    []
  );

  // Early return for invalid props - must come after all hooks
  if (!title || !subTitle || !noOfPoints || !pointsDescription || !componentID) {
    return null;
  }

  return (
    <StyledPointsDetails
      data-component-name="o-cobrand-PointsDetails"
      data-testid="cobrand-PointsDetails"
      id={uniqueId}
      className={containerClasses}
      role="region"
      aria-label={ariaLabel}
    >
      {/* Screen reader only card name for accessibility */}
      {cardName && <span className="sr-only">{cardName}</span>}

      {/* Main title heading */}
      <Heading
        titleText={title}
        variation={Types.headingType.title}
        fontSize={Types.size.medium}
        customClass={headingClasses}
      />

      {/* Content wrapper with subtitle, points, and description */}
      <div className={contentWrapperClasses}>
        {/* Subtitle heading */}
        <Heading
          titleText={subTitle}
          variation={Types.headingType.subtitle}
          fontSize={Types.size.extraLarge}
          customClass={subtitleClasses}
        />

        {/* Points display with large underlined style */}
        <div className="text-center mb-3">
          <Points text={noOfPoints} borderstyle="underlinedLarge" />
        </div>

        {/* Rich text description with HTML support */}
        <RichText text={pointsDescription} componentId={componentID} customClass={descriptionClasses} />
      </div>
    </StyledPointsDetails>
  );
});

// Display name for React DevTools
PointsDetailsComp.displayName = 'PointsDetails';

/**
 * PointsDetails - Editable wrapper component for AEM integration
 * Wraps PointsDetailsComp with EditableComponent for AEM authoring
 */
export const PointsDetails = (props: PointsDetailsProps) => {
  return (
    <EditableComponent config={PointsDetailsConfig} {...props}>
      <PointsDetailsComp {...props} />
    </EditableComponent>
  );
};
